import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone,
  MessageCircle,
  Star,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Send,
  Check,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { MaskLine, easeOut } from "@/lib/reveal";
import { CLINIC, IMAGES, whatsappLink, telLink, submitBookingForm } from "@/lib/site";
import { useLanguage } from "@/context/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyForm = {
  first_name: "",
  last_name: "",
  phone: "",
  service: "",
  date: "",
  time: "",
  message: "",
};

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const serviceOptions = t.booking?.servicesList || (t.services.items || []).map((s) => s.title);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone || !form.service) {
      toast.error(t.booking.errorFill || "Ju lutem plotësoni fushat e kërkuara.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name || "-",
        phone: form.phone,
        service: form.service,
        date: form.date || "",
        time: form.time || "",
        message: form.message || "Rezervim nga Hero Form",
      };

      await submitBookingForm(payload);

      setDone(true);
      setForm(emptyForm);
      toast.success(t.booking.successDesc || "Kërkesa u dërgua me sukses!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Ndodhi një gabim.");
    } finally {
      setLoading(false);
    }
  };

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  const inputCls =
    "w-full h-12 px-4 bg-background border-2 border-border/90 rounded-sm text-base font-medium text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent focus:bg-card focus:ring-2 focus:ring-accent/20 transition-all";

  return (
    <section
      ref={ref}
      id="hero"
      data-testid="hero"
      className="relative min-h-[95vh] lg:min-h-screen flex items-center overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24 bg-background"
    >
      {/* Background Layer with Parallax & Clear Photo Visibility */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div style={{ y }} className="absolute inset-0 w-full h-full scale-105">
          <div className="absolute inset-0">
            <img
              src={IMAGES.hero}
              alt="FizioFit Center Lushnjë - Fizioterapi dhe Rehabilitim"
              className="h-full w-full object-cover object-center opacity-85 sm:opacity-90 brightness-[0.98] contrast-[1.02]"
              loading="eager"
            />
            {/* Balanced overlay: crisp on the photo, smooth gradient on the left for text legibility */}
            <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/65 to-background/20 lg:to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/40" />
          </div>
        </motion.div>
      </div>

      {/* Hero Content Container */}
      <div className="container-x relative z-10 w-full">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-16 items-center">
          
          {/* Left Column: Branding, Value Proposition & CTAs */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: easeOut }}
              className="flex flex-wrap items-center gap-3 sm:gap-3.5 mb-6 sm:mb-8"
            >
              {/* Standout Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent text-white font-bold text-xs sm:text-sm shadow-md shadow-accent/25 tracking-wide ring-2 ring-accent/30">
                <Sparkles size={16} className="text-amber-300 animate-pulse" />
                <span>{t.hero.freeConsultBadge}</span>
              </div>

              {/* Google Reviews Trust Badge */}
              <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-foreground bg-card/95 backdrop-blur-md px-3.5 py-2 rounded-full border border-border shadow-xs">
                <Star size={15} className="fill-amber-400 text-amber-400" />
                <span className="text-sm font-extrabold">{CLINIC.rating}</span>
                <span className="text-foreground/80 font-semibold">({CLINIC.reviews} {t.hero.reviews})</span>
                <span className="text-muted-foreground font-medium hidden sm:inline">· {CLINIC.addressShort}</span>
              </span>
            </motion.div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.6rem] font-extrabold tracking-tight text-foreground leading-[1.08] sm:leading-[1.05]">
              <MaskLine delay={0.1}>{t.hero.h1_1}</MaskLine>{" "}
              <MaskLine delay={0.2} className="text-accent font-extrabold">{t.hero.h1_2}</MaskLine>{" "}
              <MaskLine delay={0.3}>{t.hero.h1_3}</MaskLine>
            </h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45, ease: easeOut }}
              className="mt-8 sm:mt-10 max-w-xl text-lg sm:text-xl text-foreground/90 font-medium leading-relaxed"
            >
              {t.hero.desc}
            </motion.p>

            {/* Quick Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: easeOut }}
              className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                data-testid="hero-cta-whatsapp"
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 h-13 sm:h-14 px-7 py-3.5 rounded-sm bg-[#25D366] text-white text-base sm:text-lg font-bold hover:bg-[#20b857] shadow-md shadow-[#25D366]/20 transition-all duration-200"
              >
                <MessageCircle size={20} /> WhatsApp Chat
              </a>

              <a
                data-testid="hero-cta-phone"
                href={telLink}
                className="inline-flex items-center gap-2.5 h-13 sm:h-14 px-6 py-3.5 rounded-sm border-2 border-border bg-card/95 backdrop-blur-sm text-foreground text-base sm:text-lg font-bold hover:border-accent hover:text-accent transition-all duration-200 shadow-xs"
              >
                <Phone size={18} className="text-accent" /> {CLINIC.phone}
              </a>

              <button
                data-testid="hero-cta-services"
                onClick={() => go("#services")}
                className="inline-flex items-center gap-2 text-sm sm:text-base font-bold text-foreground hover:text-accent ml-2 transition-colors cursor-pointer py-2"
              >
                <span>{t.hero.servicesCta}</span>
                <ArrowRight size={16} />
              </button>
            </motion.div>

            {/* Trust feature pills */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-10 sm:mt-12 pt-6 border-t-2 border-border/80 flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-foreground"
            >
              <span className="inline-flex items-center gap-2 font-bold text-foreground bg-card/95 px-3.5 py-1.5 rounded-sm border border-border shadow-xs">
                <Sparkles size={15} className="text-accent" /> {t.hero.feat_1}
              </span>
              <span className="inline-flex items-center gap-2 font-bold text-foreground bg-card/95 px-3.5 py-1.5 rounded-sm border border-border shadow-xs">
                <CheckCircle2 size={16} className="text-accent" /> {t.hero.feat_2}
              </span>
              <span className="inline-flex items-center gap-2 font-bold text-foreground bg-card/95 px-3.5 py-1.5 rounded-sm border border-border shadow-xs">
                <CheckCircle2 size={16} className="text-accent" /> {t.hero.feat_3}
              </span>
            </motion.div>
          </div>

          {/* Right Column: Hero Contact & Booking Form (Above the Fold) */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25, ease: easeOut }}
              className="relative rounded-sm border-2 border-border bg-card/98 backdrop-blur-xl p-6 sm:p-8 shadow-2xl shadow-black/5"
            >
              {/* Form Top Subtle Teal Accent Bar */}
              <div className="absolute top-0 left-6 right-6 h-[3px] bg-gradient-to-r from-transparent via-accent to-transparent" />

              {done ? (
                <div data-testid="hero-booking-success" className="py-10 text-center">
                  <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-accent text-white shadow-lg shadow-accent/30">
                    <Check size={32} />
                  </div>
                  <h3 className="mt-6 text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                    {t.booking.successTitle}
                  </h3>
                  <p className="mt-3 text-base text-foreground/80 font-medium leading-relaxed">
                    {t.booking.successDesc}
                  </p>
                  <button
                    data-testid="hero-booking-reset"
                    onClick={() => setDone(false)}
                    className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-accent hover:underline cursor-pointer"
                  >
                    {t.booking.sendAnother}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="hero-booking-form" className="space-y-4">
                  {/* Form Header */}
                  <div>
                    <div className="inline-flex items-center gap-1.5 text-xs font-extrabold tracking-wider uppercase text-accent">
                      <Sparkles size={14} /> {t.hero.freeConsultBadge}
                    </div>
                    <h3 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                      {t.booking.title}
                    </h3>
                    <p className="text-sm font-medium text-foreground/80 mt-1">
                      Plotësoni të dhënat dhe stafi ynë do t'ju kontaktojë brenda ditës.
                    </p>
                  </div>

                  {/* Name Fields (2 Columns) */}
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div>
                      <label className="text-xs sm:text-sm font-bold text-foreground mb-1.5 block">
                        {t.booking.firstName}
                      </label>
                      <input
                        data-testid="hero-booking-firstname"
                        className={inputCls}
                        value={form.first_name}
                        onChange={(e) => set("first_name", e.target.value)}
                        placeholder="Emri juaj"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-bold text-foreground mb-1.5 block">
                        {t.booking.lastName}
                      </label>
                      <input
                        data-testid="hero-booking-lastname"
                        className={inputCls}
                        value={form.last_name}
                        onChange={(e) => set("last_name", e.target.value)}
                        placeholder="Mbiemri"
                      />
                    </div>
                  </div>

                  {/* Phone Field */}
                  <div>
                    <label className="text-xs sm:text-sm font-bold text-foreground mb-1.5 block">
                      {t.booking.phone}
                    </label>
                    <input
                      type="tel"
                      data-testid="hero-booking-phone"
                      className={inputCls}
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      placeholder="+355 6X XXX XXXX"
                      required
                    />
                  </div>

                  {/* Service Field */}
                  <div>
                    <label className="text-xs sm:text-sm font-bold text-foreground mb-1.5 block">
                      {t.booking.service}
                    </label>
                    <Select value={form.service} onValueChange={(v) => set("service", v)}>
                      <SelectTrigger
                        data-testid="hero-booking-service"
                        className="h-12 rounded-sm border-2 border-border/90 bg-background text-base font-semibold text-foreground focus:border-accent"
                      >
                        <SelectValue placeholder={t.booking.servicePlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`hero-service-${s}`} className="text-sm sm:text-base font-medium py-2.5">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date & Time Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs sm:text-sm font-bold text-foreground mb-1.5 block">
                        {t.booking.date} <span className="text-xs font-normal text-muted-foreground">(Opsionale)</span>
                      </label>
                      <input
                        type="date"
                        data-testid="hero-booking-date"
                        className={inputCls}
                        value={form.date}
                        onChange={(e) => set("date", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-bold text-foreground mb-1.5 block">
                        {t.booking.time} <span className="text-xs font-normal text-muted-foreground">(Opsionale)</span>
                      </label>
                      <input
                        type="time"
                        data-testid="hero-booking-time"
                        className={inputCls}
                        value={form.time}
                        onChange={(e) => set("time", e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={loading}
                    data-testid="hero-booking-submit"
                    className="w-full inline-flex items-center justify-center gap-2.5 h-13 sm:h-14 rounded-sm bg-accent text-white text-base sm:text-lg font-bold hover:bg-accent/90 shadow-lg shadow-accent/25 transition-all duration-200 disabled:opacity-60 cursor-pointer mt-2"
                  >
                    {loading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                    {loading ? t.booking.submitting : "Dërgo Kërkesën për Konsultë Falas"}
                  </motion.button>

                  {/* Trust Footer Note */}
                  <div className="pt-2 flex items-center justify-center gap-2 text-xs font-semibold text-foreground/80 text-center">
                    <ShieldCheck size={16} className="text-accent" />
                    <span>Të dhënat tuaja janë 100% të sigurta · Pa pagesë paraprake</span>
                  </div>
                </form>
              )}
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
};

